/**
 * The 'boardroom' theme — the credible executive default (token-contract §5.2,
 * "Boardroom" in the THEME-picker rail; the recommended theme for most decks).
 *
 * This is a COMPLETE theme (the model is `xavior.ts`): every required
 * ArtTokenMap key, every §2.6 layout token, tokens for all 6 required treatments
 * + the 4 optional treatments, the logo slot, and a richer `layoutCss` block for
 * per-treatment geometry that the token-only path cannot express.
 *
 * DESIGN WORLD — Boardroom is the deck a CFO trusts: confident, structured,
 * premium-corporate. A near-white paper canvas with strong ink contrast and a
 * single restrained DEEP-NAVY accent (institutional, never bright, never
 * playful). The look is disciplined and data-forward — IBM Plex Sans (a clean
 * professional grotesque with corporate heritage) for both display and body,
 * IBM Plex Mono for tabular stat numerals so the figures read like a financial
 * statement. Generous, even spacing and clear hierarchy do the work; ornament is
 * deliberately absent.
 *
 * STRUCTURALLY DISTINCT LAYOUT (§2.6) — Boardroom is NOT a recolor of Xavior:
 *   • larger base `space-unit` (14px vs 8px) → roomier, board-paper rhythm
 *   • larger `gutter` (88px) → confident edge inset on a slide
 *   • moderate `radius`/`radius-sm` (7px / 4px) → soft-cornered but corporate
 *   • tighter `line-height` (1.55) + `line-height-display` (1.15) → dense,
 *     report-like prose under crisp headings
 *   • near-zero display tracking (-0.01em) + restrained eyebrow tracking (0.14em)
 *   • narrower `measure` (680px) → a disciplined single column
 *   • moderate `stat-size` (clamp(2.75rem,6vw,4.75rem)) → a hero figure that
 *     stays authoritative rather than theatrical (cf. Keynote's 7rem).
 */
export const boardroom = {
    id: 'boardroom',
    label: 'Boardroom',
    layoutLanguage: 'keynote',
    fontsUrl: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap',
    tokens: {
        // ── 2.1 typography ──
        '--xv-art-font-display': "'IBM Plex Sans', 'Helvetica Neue', Arial, system-ui, sans-serif",
        '--xv-art-font-body': "'IBM Plex Sans', 'Helvetica Neue', Arial, system-ui, sans-serif",
        '--xv-art-font-mono': "'IBM Plex Mono', ui-monospace, Menlo, monospace",
        '--xv-art-font-weight-display': '600',
        '--xv-art-font-weight-body': '400',
        // Crisp, near-neutral display tracking — institutional, not theatrical.
        '--xv-art-tracking-display': '-0.01em',
        // Restrained eyebrow tracking — wide enough to read as a kicker, not loud.
        '--xv-art-tracking-eyebrow': '0.14em',
        // ── 2.2 surface ──
        // Warm-neutral paper, not pure white — reads as premium stock, not screen.
        '--xv-art-bg': '#fcfcfb',
        '--xv-art-bg-surface': '#f4f5f7',
        // The dark/contrast slide: deep navy-black, on-brand with the navy accent.
        '--xv-art-bg-inverse': '#0f1d2e',
        '--xv-art-surface-hover': '#eceef1',
        // ── 2.3 ink ──
        // Strong near-black ink with a faint navy cast — high contrast, authoritative.
        '--xv-art-ink': '#101826',
        '--xv-art-ink-secondary': '#41506a',
        '--xv-art-ink-muted': '#8190a6',
        '--xv-art-ink-inverse': '#eef2f7',
        // ── 2.4 accent ──
        // Restrained DEEP NAVY — the single brand accent; ink-toned, never bright.
        '--xv-art-accent': '#1e3a5f',
        '--xv-art-accent-ink': '#ffffff',
        '--xv-art-accent-dim': 'rgba(30, 58, 95, 0.10)',
        // A muted steel-teal as the chart-only secondary series color (never text).
        '--xv-art-accent-2': '#5b7a99',
        // ── 2.5 borders ──
        '--xv-art-border': '#dde1e8',
        '--xv-art-border-strong': '#bcc4d1',
        // ── 2.6 layout (the structural identity — DISTINCT from every other theme) ──
        // Disciplined single column, narrower than Xavior's 780px.
        '--xv-art-measure': '680px',
        // Roomier base unit → board-paper rhythm (Xavior 8 / Mono 8 / Boardroom 14).
        '--xv-art-space-unit': '14px',
        // Confident edge inset on a slide.
        '--xv-art-gutter': '88px',
        // Moderate corporate radius — soft, not rounded-friendly.
        '--xv-art-radius': '7px',
        '--xv-art-radius-sm': '4px',
        // Dense, report-like prose under crisp display.
        '--xv-art-line-height': '1.55',
        '--xv-art-line-height-display': '1.15',
        // ── 2.7 stat primitives ──
        // Stat numerals track the resolved ink (#5339) — a brand kit's ink override
        // reaches the hero figure; renders byte-identical without a kit.
        '--xv-art-stat-ink': 'var(--xv-art-ink, #101826)',
        '--xv-art-stat-positive': '#1f7a4d',
        '--xv-art-stat-negative': '#b4232a',
        // Authoritative but not theatrical hero figure.
        '--xv-art-stat-size': 'clamp(2.75rem, 6vw, 4.75rem)',
        // ── 3.1 highlight — restrained color-only emphasis in the navy accent ──
        // Track the resolved accent (default = the navy below) so an applied brand
        // kit's accent override reaches inline emphasis; renders byte-identical navy
        // with no kit (§5.4 "brand kit wins where it overlaps; theme keeps layout").
        '--xv-art-highlight-ink': 'var(--xv-art-accent, #1e3a5f)',
        '--xv-art-highlight-bg': 'transparent',
        '--xv-art-highlight-weight': '600',
        '--xv-art-highlight-pad': '0',
        // ── 3.2 callout — quiet panel, navy insight bar; warning/success muted ──
        '--xv-art-callout-bg': '#f4f5f7',
        '--xv-art-callout-border': '#dde1e8',
        // Callout body ink tracks the resolved ink (#5339).
        '--xv-art-callout-ink': 'var(--xv-art-ink, #101826)',
        '--xv-art-callout-radius': '7px',
        // Track the resolved accent (default = navy) so an applied brand kit's accent
        // override reaches the insight bar; renders byte-identical navy with no kit.
        '--xv-art-callout-accent-insight': 'var(--xv-art-accent, #1e3a5f)',
        // Muted ochre, not the loud default #f59e0b — keeps the corporate register.
        '--xv-art-callout-accent-warning': '#b5840f',
        '--xv-art-callout-accent-success': '#1f7a4d',
        // ── 3.3 stat — tabular Plex Mono numerals, uppercase labels ──
        // Stat labels track the resolved muted ink (#5352).
        '--xv-art-stat-label-ink': 'var(--xv-art-ink-muted, #8190a6)',
        '--xv-art-stat-label-tracking': '0.1em',
        // Numerals in Plex Mono → tabular, financial-statement feel.
        '--xv-art-stat-numeral-font': "'IBM Plex Mono', ui-monospace, monospace",
        // ── 3.4 quote — sans, normal (not italic): a measured executive pull ──
        // Quote ink tracks the resolved secondary ink (#5339).
        '--xv-art-quote-ink': 'var(--xv-art-ink-secondary, #41506a)',
        // Accent-tracking left rule (navy default; a kit's accent flows through).
        '--xv-art-quote-bar': 'var(--xv-art-accent, #1e3a5f)',
        '--xv-art-quote-font': "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif",
        '--xv-art-quote-size': '1.35rem',
        '--xv-art-quote-style': 'normal',
        // ── 3.5 eyebrow — navy rule + muted uppercase kicker ──
        // The eyebrow kicker tracks the resolved muted ink (#5352).
        '--xv-art-eyebrow-ink': 'var(--xv-art-ink-muted, #8190a6)',
        '--xv-art-eyebrow-tracking': '0.14em',
        '--xv-art-eyebrow-size': '0.72rem',
        '--xv-art-eyebrow-transform': 'uppercase',
        '--xv-art-eyebrow-rule': 'var(--xv-art-accent, #1e3a5f)',
        // ── 3.6 wordmark — quiet, confident, semibold ──
        // The footer wordmark tracks the resolved secondary ink (#5339).
        '--xv-art-wordmark-ink': 'var(--xv-art-ink-secondary, #41506a)',
        '--xv-art-wordmark-font': "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif",
        '--xv-art-wordmark-size': '0.85rem',
        '--xv-art-wordmark-weight': '600',
        // ── 3.7 optional treatments ──
        // Links track the resolved accent (navy default; a kit's accent flows through).
        '--xv-art-link-ink': 'var(--xv-art-accent, #1e3a5f)',
        '--xv-art-code-bg': '#f4f5f7',
        '--xv-art-code-ink': 'var(--xv-art-accent, #1e3a5f)',
        // ── 4.2 logo slot (theme leaves logo empty → text wordmark renders) ──
        '--xv-art-logo-url': 'none',
        '--xv-art-logo-aspect': 'auto',
        '--xv-art-logo-height': '0.85rem',
        '--xv-art-logo-invert': '0',
    },
    /**
     * Richer per-treatment geometry the token-only path can't express. Scoped under
     * [data-xv-theme="boardroom"]; the token-only render is still coherent without
     * it. The intent is institutional discipline: a navy tick before the eyebrow,
     * a numbered eyebrow index, a firm rule under headings, a strong (not playful)
     * left bar on callouts/quotes, accent-square crispness beside the hero stat,
     * tabular stat numerals, and an uppercase wordmark with a navy lead-rule —
     * the same restrained system whether it renders as a deck slide, a doc
     * section, or a newsletter block.
     */
    layoutCss: `
[data-xv-theme="boardroom"] {
  /* Board-paper rhythm: generous, even vertical spacing between blocks. */
  --xv-art-block-gap: calc(var(--xv-art-space-unit) * 2.5);
}

/* Eyebrow — a short navy tick + numeric index, uppercase muted kicker. */
[data-xv-theme="boardroom"] .xv-art-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--xv-art-space-unit) * 0.85);
  font-weight: 600;
  line-height: 1;
}
[data-xv-theme="boardroom"] .xv-art-eyebrow::before {
  content: '';
  display: inline-block;
  width: 26px;
  height: 2px;
  background: var(--xv-art-eyebrow-rule, var(--xv-art-accent));
}

/* Display headings — crisp, dense leading, a firm hairline beneath. */
[data-xv-theme="boardroom"] .xv-art-headline,
[data-xv-theme="boardroom"] h1,
[data-xv-theme="boardroom"] h2 {
  letter-spacing: var(--xv-art-tracking-display, -0.01em);
  line-height: var(--xv-art-line-height-display, 1.15);
}
[data-xv-theme="boardroom"] .xv-art-headline {
  padding-bottom: calc(var(--xv-art-space-unit) * 1.1);
  border-bottom: 2px solid var(--xv-art-border-strong);
}

/* Hero stat — large but composed; tabular mono numerals; crisp accent square. */
[data-xv-theme="boardroom"] .xv-art-stat {
  font-family: var(--xv-art-stat-numeral-font, var(--xv-art-font-display));
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  line-height: 1.02;
}
[data-xv-theme="boardroom"] .xv-art-stat__label {
  font-family: var(--xv-art-font-body);
  text-transform: uppercase;
  letter-spacing: var(--xv-art-stat-label-tracking, 0.1em);
  font-size: 0.7rem;
  font-weight: 600;
}
[data-xv-theme="boardroom"] .xv-art-stat__trend {
  font-family: var(--xv-art-stat-numeral-font, var(--xv-art-font-display));
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

/* Two-stat band — even columns, hairline divider, generous gutter. */
[data-xv-theme="boardroom"] .xv-art-stats-band {
  gap: calc(var(--xv-art-space-unit) * 3);
}

/* Callout — quiet surface, a STRONG 4px navy left bar (insight); square corners
   on the bar, rounded on the panel. Disciplined, not decorative. */
[data-xv-theme="boardroom"] .xv-art-callout {
  border: 1px solid var(--xv-art-callout-border, var(--xv-art-border));
  border-left: 4px solid var(--xv-art-callout-accent-insight, var(--xv-art-accent));
  border-radius: var(--xv-art-callout-radius, var(--xv-art-radius));
  padding: calc(var(--xv-art-space-unit) * 1.6) calc(var(--xv-art-space-unit) * 1.8);
}
[data-xv-theme="boardroom"] .xv-art-callout--warning {
  border-left-color: var(--xv-art-callout-accent-warning);
}
[data-xv-theme="boardroom"] .xv-art-callout--success {
  border-left-color: var(--xv-art-callout-accent-success);
}

/* Quote — a measured executive pull: navy left rule, sans, normal (not italic),
   tightened leading. No oversized quotation glyph — restraint over flourish. */
[data-xv-theme="boardroom"] .xv-art-quote {
  border-left: 3px solid var(--xv-art-quote-bar, var(--xv-art-accent));
  padding-left: calc(var(--xv-art-space-unit) * 1.6);
  font-style: var(--xv-art-quote-style, normal);
  line-height: 1.4;
  font-weight: 400;
}

/* Wordmark — uppercase, tracked, with a short navy lead-rule. Reads as a
   masthead in a deck footer, a doc footer, and an email footer alike. */
[data-xv-theme="boardroom"] .xv-art-wordmark {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--xv-art-space-unit) * 0.7);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: var(--xv-art-wordmark-weight, 600);
}
[data-xv-theme="boardroom"] .xv-art-wordmark::before {
  content: '';
  display: inline-block;
  width: 18px;
  height: 2px;
  background: var(--xv-art-accent);
}

/* Inline highlight — color-only navy emphasis; no marker pill (corporate). */
[data-xv-theme="boardroom"] .xv-art-highlight {
  color: var(--xv-art-highlight-ink, var(--xv-art-accent));
  font-weight: var(--xv-art-highlight-weight, 600);
}

/* Links — navy, with a subtle underline offset for a printed-report feel. */
[data-xv-theme="boardroom"] .xv-art-link {
  color: var(--xv-art-link-ink, var(--xv-art-accent));
  text-underline-offset: 0.18em;
  text-decoration-thickness: 1px;
}

/* Inverse / dark slide — keep the navy world, lift ink and rules. */
[data-xv-theme="boardroom"] .xv-art--inverse {
  background: var(--xv-art-bg-inverse);
  color: var(--xv-art-ink-inverse);
}
[data-xv-theme="boardroom"] .xv-art--inverse .xv-art-eyebrow::before,
[data-xv-theme="boardroom"] .xv-art--inverse .xv-art-wordmark::before {
  background: var(--xv-art-accent-2);
}
`,
};
//# sourceMappingURL=boardroom.js.map